import { useContext, useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setChatVisible } from "@/redux/reducers/chat";
import { FuelContext } from "@/providers/fuel-provider";
import { useChatTracking } from "./useChatTracking";
import isClientSide from "@/js/helpers/isClientSide";
import { CHAT_TYPES } from "@/js/constants/chat";
import { coerceMonarchOptionalString } from "@/js/helpers/unwrapMonarchScalar";
import { useChatTriggers } from "./triggers/useChatTriggers";
import { useKoreChatIconInit } from "./useKoreChatIconInit";
import { findKoreChatRoot } from "@/hooks/chat/helpers/chatbotHelpers";

export const useChat = () => {
  const dispatch = useDispatch();
  const { chatConfig } = useContext(FuelContext);
  const { chatType, sdkVersion } = chatConfig;
  const isVisible = useSelector((state) => state.chat.visible);
  const triggerOverrides = useSelector((state) => state.chat.triggerOverrides);
  const [chatButton, setChatButton] = useState(null);

  useKoreChatIconInit({ chatConfig, chatType, triggerOverrides });

  const lastTriggerFired = useChatTriggers(chatConfig);

  useChatTracking({
    chatButton,
    isVisible,
    chatType,
    lastTriggerFired,
  });

  useEffect(() => {
    if (chatType === CHAT_TYPES.GIGA) dispatch(setChatVisible(true));
    if (chatType === CHAT_TYPES.NONE) dispatch(setChatVisible(false));
  }, [chatType, dispatch]);

  useEffect(() => {
    const syncRoot = () => setChatButton(findKoreChatRoot());

    syncRoot();

    const observer = new MutationObserver(() => {
      syncRoot();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!chatButton) return;
    chatButton.style.display = isVisible ? "block" : "none";
  }, [chatButton, isVisible]);

  const koreSdkVersionParam = useMemo(() => {
    if (!isClientSide()) return null;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("koreSdkVersion");
  }, []);

  const resolvedSdkVersion = useMemo(() => {
    const fromQuery = koreSdkVersionParam?.trim() || null;
    const fromConfig = coerceMonarchOptionalString(sdkVersion);
    const raw =
      fromQuery ||
      fromConfig ||
      (typeof sdkVersion === "string" ? sdkVersion : null);
    const effective =
      typeof raw === "string" ? raw : coerceMonarchOptionalString(raw);
    return effective;
  }, [koreSdkVersionParam, sdkVersion]);

  const resizeChatButton = () => {
    let attempts = 0;
    const timer = setInterval(() => {
      const koreChatWindow = document.querySelector('[id*="kore-chat-window"]');
      if (koreChatWindow) {
        const minimizeEl = koreChatWindow.querySelector(".minimized");
        if (minimizeEl && minimizeEl instanceof HTMLElement) {
          minimizeEl.style.setProperty("width", "0px", "important");
        }
      }
      attempts += 1;
      if (attempts >= 5) clearInterval(timer);
    }, 500);
  };

  return useMemo(
    () => ({
      sdkVersion: resolvedSdkVersion,
      chatType,
      resizeChatButton,
    }),
    [resolvedSdkVersion, chatType],
  );
};
